import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@mui/material';

import { API_CONFIG } from 'shared/constants/api';
import AMBGButton from 'shared/components/AMBGButton/AMBGButton';
import HttpService from 'shared/services/Http.service';
import { ROUTES } from 'shared/constants/constant';

import { StyledAuthTitle } from 'assets/styles/Common.style';

const VerifyAccountScreen: FC = () => {
	const { token, userId } = useParams();
	const navigate = useNavigate();
	const [isLoading, setLoading] = useState<boolean>(false);
	const [verifyUserId, setVerifyUserId] = useState<string>('');
	const [isVerifySuccess, setVerifySuccess] = useState<boolean>(false);
	const [isResendEnable, setResendEnable] = useState<boolean>(false);
	const [isResendSuccess, setResendSuccess] = useState<boolean>(false);
	const [isInvalidToken, setInvalidToken] = useState<boolean>(false);

	const verifyEmail = useCallback(async (token: string, userId: string) => {
		try {
			setLoading(true);
			const params = {
				token: token,
				user_id: userId
			};
			await HttpService.post(API_CONFIG.verifyAccount, params);
			setLoading(false);
			setVerifySuccess(true);
		} catch (error: any) {
			if (error.response.status === 400) {
				setResendEnable(true);
			} else if (error.response.status === 404) {
				setInvalidToken(true);
			}
			setLoading(false);
		}
	}, []);

	const resendVerification = useCallback(async () => {
		try {
			setLoading(true);
			const params = {
				user_id: verifyUserId
			};
			await HttpService.post(API_CONFIG.resendVerificationEmail, params);
			setLoading(false);
			setResendSuccess(true);
		} catch (error) {
			setLoading(false);
		}
	}, [verifyUserId]);

	useEffect(() => {
		if (token && userId) {
			verifyEmail(token, userId);
			setVerifyUserId(userId);
		} else {
			navigate(ROUTES.LOGIN);
		}
	}, [token, userId, verifyEmail, navigate]);

	return (
		<>
			<StyledAuthTitle data-testid='resetPasswordTitle'>Account Verification</StyledAuthTitle>
			{isInvalidToken && (
				<>
					<Alert variant='outlined' severity='error' sx={{ marginBottom: '20px' }}>
						Token is invalid.
					</Alert>
					<AMBGButton
						data-testid='loginButton'
						fullWidth
						text='Go To Login'
						onClick={() => navigate(ROUTES.LOGIN)}
						loading={isLoading}
					/>
				</>
			)}
			{isVerifySuccess && !isResendEnable && (
				<>
					<Alert variant='outlined' severity='success' sx={{ marginBottom: '20px' }}>
						Your account verified successfully.
					</Alert>
					<AMBGButton
						data-testid='loginButton'
						fullWidth
						text='Go To Login'
						onClick={() => navigate(ROUTES.LOGIN)}
						loading={isLoading}
					/>
				</>
			)}
			{isResendEnable && !isResendSuccess && (
				<>
					<Alert variant='outlined' severity='error' sx={{ marginBottom: '20px' }}>
						Account verification failed. Click the button below to refresh token.
					</Alert>
					<AMBGButton
						data-testid='refreshTokenButton'
						fullWidth
						text='Refresh Token'
						onClick={resendVerification}
						loading={isLoading}
					/>
				</>
			)}
			{isResendSuccess && (
				<>
					<Alert variant='outlined' severity='success' sx={{ marginBottom: '20px' }}>
						Email sent successfully. Please check your email.
					</Alert>
				</>
			)}
		</>
	);
};

export default VerifyAccountScreen;
